import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import { AuthenticationService } from '@flexyoo/auth';
import { WhiteLabelService } from '@flexyoo/common';
import { FlexyooVersionCheckService } from '@flexyoo/core';
import { EmployeeBenefitFamily } from '@flexyoo/employee/models';
import { MbpEmployeeBenefitFamily } from '@flexyoo/mbp/components';
import { BehaviorSubject, takeUntil } from 'rxjs';
import { filter } from 'rxjs/operators';
import { SignalrService } from './services/signal-r.service';
import { NgOnDestroy } from './shared/ng-on-destroy.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  standalone: false,
  providers: [NgOnDestroy],
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly signalrService = inject(SignalrService);
  private readonly onDestroy$ = inject(NgOnDestroy, { self: true });

  protected readonly isLoadingNavigation$ = new BehaviorSubject(false);
  protected readonly isVersionCheckInProgress$ = new BehaviorSubject(false);

  constructor(
    private wlc: WhiteLabelService,
    private auth: AuthenticationService,
    private readonly iconRegistry: MatIconRegistry,
    private readonly sanitizer: DomSanitizer,
    private readonly router: Router,
    private readonly versionCheckService: FlexyooVersionCheckService
  ) {
    this.signalrService.startConnection();

    this.isVersionCheckInProgress$.next(true);
    this.versionCheckService
      .checkAppVersion(true, window.location.pathname ?? '')
      .pipe(filter((isUpdated) => !isUpdated))
      .subscribe(() => this.isVersionCheckInProgress$.next(false));
    this.versionCheckService.handleNavigationErrors();

    this.router.events.pipe(takeUntil(this.onDestroy$)).subscribe((ev) => {
      if (ev instanceof NavigationStart) {
        this.isLoadingNavigation$.next(true);
      }

      if (
        ev instanceof NavigationEnd ||
        ev instanceof NavigationCancel ||
        ev instanceof NavigationError
      ) {
        this.isLoadingNavigation$.next(false);
      }
    });
  }

  public ngOnInit(): void {
    this.wlc.applyTheme(this.auth.getCurrentUser());
    this.initBenefitsIcons();
  }

  public ngOnDestroy(): void {
    this.auth.stopUserIdleWatching();
    this.signalrService.stopConnection();
  }

  private initBenefitsIcons(): void {
    for (let benefitTypeId = 2; benefitTypeId <= 24; ++benefitTypeId) {
      this.iconRegistry.addSvgIcon(
        `benefit-type-${benefitTypeId}`,
        this.sanitizer.bypassSecurityTrustResourceUrl(
          `./assets/images/benefits-types/cat-${benefitTypeId}.svg`
        )
      );
    }

    const benefitFamilies: EmployeeBenefitFamily[] = [
      'finance',
      'mobile',
      'multimedia',
      'work-life',
    ];
    benefitFamilies.forEach((benefitFamily) =>
      this.iconRegistry.addSvgIcon(
        `benefit-family-${benefitFamily}`,
        this.sanitizer.bypassSecurityTrustResourceUrl(
          `./assets/images/benefits-families/fam-${benefitFamily}.svg`
        )
      )
    );

    // Mobility Budget
    this.iconRegistry.addSvgIcon(
      `mbp-benefit-type-2`,
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/images/mbp-benefits-types/2.svg`)
    );
    for (let benefitTypeId = 25; benefitTypeId <= 30; ++benefitTypeId) {
      this.iconRegistry.addSvgIcon(
        `mbp-benefit-type-${benefitTypeId}`,
        this.sanitizer.bypassSecurityTrustResourceUrl(
          `./assets/images/mbp-benefits-types/${benefitTypeId}.svg`
        )
      );
    }

    const mbpBenefitFamilies: MbpEmployeeBenefitFamily[] = ['mbp-car', 'mbp-mobility', 'mbp-cash'];
    mbpBenefitFamilies.forEach((benefitFamily) =>
      this.iconRegistry.addSvgIcon(
        `benefit-family-${benefitFamily}`,
        this.sanitizer.bypassSecurityTrustResourceUrl(
          `./assets/images/mbp-benefits-families/${benefitFamily}.svg`
        )
      )
    );

    // Extra icons
    this.iconRegistry.addSvgIcon(
      'fo-cafeteria-plan',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/cafeteria-plan.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-person',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/person.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-group',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/group.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-budgets',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/account_balance_wallet.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-expand-arrows',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/expand-arrows.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-warn',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/warn.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-upload',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/upload.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-download',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/download.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-tray-download',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/tray-download.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-tray-upload',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/tray-upload.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-error-circle',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/error_circle.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-check-circle',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/check_circle.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-close-circle',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/close_circle.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-list-checked',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/list_checked.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-opco',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/opco.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-settings',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/settings.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-statistics',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/statistics.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-tools',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/tools.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-info',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/info.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-trash',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/trash.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-cart-confirm',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/cart-confirm.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-tco',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/tco.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-edit',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/edit.svg`)
    );
    // Notifications
    this.iconRegistry.addSvgIcon(
      'fo-addendum',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/notifications/addendum.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-benefit',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/notifications/benefit.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-doc',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/notifications/doc.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-payroll',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/notifications/payroll.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-period',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/notifications/period.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-sync',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/notifications/sync.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-dashboard',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/dashboard.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-bell-off',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/bell_off.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-bell',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/bell.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-people',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/people.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-calendar',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/calendar.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-calendar-pending',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/calendar-pending.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-email',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/email.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-email-settings',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/email-settings.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-info-guard',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/info_guard.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-filters',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/filters.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-restore-filters',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/restore-filters.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-back-top',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/back_top.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-checkbook',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/checkbook.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-hourglasses',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/hourglasses.svg`)
    );
    this.iconRegistry.addSvgIcon(
      'fo-connection',
      this.sanitizer.bypassSecurityTrustResourceUrl(`./assets/svg/connection.svg`)
    );
  }
}
